import React, { useState, useEffect } from 'react';
import { Database, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { checkSupabaseConnection } from '../lib/supabase';
import { isOfflineModeEnabled, setOfflineMode } from '../lib/offlineStorage';

export const OfflineStatusBar: React.FC = () => {
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [offlineMode, setOfflineModeState] = useState(isOfflineModeEnabled());
  const [pendingChanges, setPendingChanges] = useState(0);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);

  useEffect(() => {
    // Check if we're online
    const checkOnlineStatus = async () => {
      // Prevent multiple simultaneous connection checks
      if (isCheckingConnection) return;
      
      setIsCheckingConnection(true);
      try {
        const online = await checkSupabaseConnection();
        setIsConnected(online);
        setConnectionError(null);
      } catch (error) {
        setIsConnected(false);
        if (error instanceof Error) {
          setConnectionError(error.message);
        } else {
          setConnectionError('Failed to connect to database');
        }
      } finally {
        setIsCheckingConnection(false);
      }
    };
    
    checkOnlineStatus();
    
    // Check for pending changes
    const syncStatus = getSyncStatus();
    setPendingChanges(syncStatus.pendingChanges);
    
    // Set up interval to check for changes
    const interval = setInterval(() => {
      const status = getSyncStatus();
      setPendingChanges(status.pendingChanges);
      setOfflineModeState(isOfflineModeEnabled());
      if (!isCheckingConnection) {
        checkOnlineStatus();
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [isCheckingConnection]);

  const handleSync = async () => {
    if (!isOnline) return;
    
    setIsChecking(true);
    try {
      await syncOfflineChanges();
      const status = getSyncStatus();
      setPendingChanges(status.pendingChanges);
      setConnectionError(null);
    } catch (error) {
      if (error instanceof Error) {
        setConnectionError(error.message);
      } else {
        setConnectionError('Failed to sync changes');
      }
    } finally {
      setIsChecking(false);
    }
  };

  const toggleOfflineMode = () => {
    const newMode = !offlineMode;
    setOfflineModeState(newMode);
    setOfflineMode(newMode);
  };

  // If we're not in offline mode or there are no pending changes, and no connection error, don't show anything
  if (!offlineMode && pendingChanges === 0 && !connectionError) {
    return null;
  }

  return (
    <div className="bg-amber-100 text-amber-700 py-2 px-4 flex items-center justify-center">
      <Database className="h-4 w-4 mr-2" />
      
      {offlineMode ? (
        <>
          <span className="text-sm">Offline-Modus aktiv - Änderungen werden lokal gespeichert</span>
          {isConnected && (
            <button 
              onClick={toggleOfflineMode}
              className="ml-2 text-xs bg-amber-200 hover:bg-amber-300 px-2 py-0.5 rounded transition-colors"
            >
              Online-Modus aktivieren
            </button>
          )}
        </>
      ) : pendingChanges > 0 ? (
        <>
          <span className="text-sm">{pendingChanges} ungespeicherte Änderungen</span>
          <button 
            onClick={handleSync}
            disabled={isChecking || !isConnected}
            className="ml-2 text-xs bg-amber-200 hover:bg-amber-300 px-2 py-0.5 rounded transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isChecking ? (
              <>
                <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                Synchronisiere...
              </>
            ) : (
              'Jetzt synchronisieren'
            )}
          </button>
        </>
      ) : null}
      
      {!isConnected && (
        <div className="ml-2 flex items-center">
          <AlertCircle className="h-3.5 w-3.5 mr-1 text-red-500" />
          <span className="text-xs text-red-500">
            {connectionError || 'Keine Verbindung'}
          </span>
        </div>
      )}
    </div>
  );
};

// Dummy functions for the component to work
// These would be imported from the actual module in a real implementation
const getSyncStatus = () => ({
  lastSync: 0,
  pendingChanges: 0,
  failedChanges: 0
});

const syncOfflineChanges = async () => {
  // This would be the actual implementation
  return Promise.resolve();
};